<template>
    <div id="auth-modal-backdrop" class="modal-backdrop" @click="handleBackdropClick">
        <div id="auth-modal-card" class="modal-card" :class="{ open: data.ready }">
            <button id="close-auth-modal-btn" class="close-btn" aria-label="Close modal" @click="close">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
            <div class="content">
                <div class="header">
                    <h3 class="title">{{ t('pages.main.modal.title') }}</h3>
                    <p class="subtitle">{{ t('pages.main.modal.subtitle') }}</p>
                </div>
                <div class="modal-instructions">
                    <p class="intro">{{ t('pages.main.modal.instructionIntro') }}</p>
                    <ol class="list">
                        <li v-for="(step, idx) in tm('pages.main.modal.steps')" :key="idx">
                            <span>
                                {{ step }}

                                <Icon v-if="data.icons[idx]"
                                    :icon="data.icons[idx].icon"
                                    :sprite="data.icons[idx].sprite"
                                />
                            </span>
                        </li>
                    </ol>
                </div>
                <div class="hero-pairing-code">
                    <span class="label">{{ t('pages.main.modal.pairingCodeLabel') }}</span>
                    <div class="value">{{ user.externalCode }}</div>
                    <div class="track" v-if="user.externalCode">
                        <div class="bar" :style="{ width: `${data.codeProgressPct}%` }"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { onMounted, onBeforeUnmount, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';
// import draggable from 'vuedraggable'

// import BaseModal from '🔗/components/modals/BaseModal.vue';
// import Button from '🔗/components/ui/Button.vue';
import Icon from '🔗/components/ui/Icon.vue';
// import ReleasePreview from '🔗/components/ui/ReleasePreview.vue';
// import EmptyLabel from '🔗/components/ui/EmptyLabel.vue';
// import TrackPreview from '🔗/components/ui/TrackPreview.vue';
// import { delay } from '🔗/utils/utils';

// import api from '🔗/api';
// import stores from '@/stores';
const user = stores.user();
const modals = stores.modals();
const appPlugin = stores.appPlugin();

const { t, tm } = useI18n();

const props = defineProps({
    name: {
        type: String,
        default: 'auth'
    }
});

const data = reactive({
    codeProgressPct: 100,
    countdownSeconds: 5 * 60,
    ready: false,
    icons: {
        2: {
            icon: 'favorite',
            sprite: 'actions'
        },
        3: {
            icon: 'settings',
            sprite: 'sections'
        }
    }
});


const stopCountdown = () => {
    if (data.countdownInterval) {
        clearInterval(data.countdownInterval);
        data.countdownInterval = null;
    }
}

const startCountdown = () => {

    data.countdownSeconds = 5 * 60;

    stopCountdown();
    data.countdownInterval = setInterval(() => {
        data.countdownSeconds--;

        if (data.countdownSeconds.value <= 0) {
            updateCode();
        } else {
            data.codeProgressPct = Math.max(0, (data.countdownSeconds / (5 * 60)) * 100);
        }
    }, 1000);
}

const checkCode = async () => {
    const result = await user.checkExternalLogin();

    if (!result) {
        clearTimeout(data.checkCodeTimeout);
        data.checkCodeTimeout = setTimeout(() => {
            checkCode();
        }, 4 * 1000); // Каждые 4 секунды надо запрашивать статус
        return;
    }

    stopCountdown();

    await user.getArtists();

    appPlugin.syncProfiles();

    close();
}

const updateCode = async () => {
    await user.createExternalLogin();

    startCountdown();

    setTimeout(() => {
        checkCode();
    }, 5 * 1000);
}

const close = () => {
    modals.close(props.name);
}

const handleBackdropClick = (e) => {
    if (e.target.classList.contains('modal-backdrop')) {
        close();
    }
}

onBeforeUnmount(() => {
    stopCountdown();
    clearTimeout(data.checkCodeTimeout);
})

onMounted(() => {
    updateCode();
    // data.id = modals.getData(props.name);
    data.ready = true;
});
</script>

<style lang="scss" scoped>
@use '@/assets/scss/variables.scss' as *;

.modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 999999;
    @include bapps.flex-center;
    padding: 1.5rem;
    background-color: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    @include bapps.transition-smooth(opacity, 0.3s);
}

.modal-card {
    position: relative;
    width: 100%;
    max-width: 32rem;
    background-color: #ffffff;
    border-radius: 2.25rem;
    box-shadow: $shadow-modal;
    padding: 2rem;
    color: $color-charcoal;
    border: 1px solid $color-border-light;
    @include bapps.transition-smooth(transform opacity, 0.25s);
    transform: scale(0.95);
    opacity: 0;
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    @include bapps.respond-to("sm") {
        padding: 2.5rem;
    }

    &.open {
        transform: scale(1);
        opacity: 1;
    }

    .close-btn {
        @include bapps.button-reset;
        position: absolute;
        top: 1.75rem;
        right: 1.75rem;
        padding: 0.625rem;
        border-radius: 9999px;
        color: $color-subtle;
        @include bapps.transition-smooth;

        svg {
            width: 1.25rem;
            height: 1.25rem;
        }

        &:hover {
            color: $color-charcoal;
            background-color: rgba(0, 0, 0, 0.04);
        }
    }

    .header {
        padding-right: 2rem;
    }

    .title {
        font-family: $font-display;
        font-weight: 900;
        font-size: 1.5rem;
        color: $color-charcoal;
        letter-spacing: -0.02em;
    }

    .subtitle {
        font-size: 0.8125rem;
        color: $color-subtle;
        margin-top: 0.25rem;

        @include bapps.respond-to("sm") {
            font-size: 0.875rem;
        }
    }

    .content {
        display: flex;
        flex-direction: column;
        gap: 1.5rem;
    }
}

.modal-instructions {
    background-color: rgba(248, 249, 250, 0.7);
    border-radius: 1.5rem;
    padding: 1.5rem;
    border: 1px solid $color-border-subtle;
    display: flex;
    flex-direction: column;
    gap: 0.875rem;

    @include bapps.respond-to("sm") {
        padding: 1.75rem;
    }

    .intro {
        font-size: 0.8125rem;
        font-weight: 700;
        color: $color-charcoal;

        @include bapps.respond-to("sm") {
            font-size: 0.875rem;
        }
    }

    .list {
        font-size: 0.8125rem;
        color: $color-text-medium;
        line-height: 1.6;
        padding-left: 1.25rem;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        @include bapps.respond-to("sm") {
            font-size: 0.875rem;
        }

        li {
            span {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 4px;
                svg {
                    width: 16px;
                    height: 16px;
                }
            }
        }

        li::marker {
            font-weight: 700;
            color: $color-charcoal;
        }
    }
}

.hero-pairing-code {
    background-color: $color-canvas;
    border-radius: 1.5rem;
    padding: 1.5rem 1rem;
    border: 1px solid $color-border-subtle;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    min-height: 179px;

    @include bapps.respond-to("sm") {
        padding: 1.75rem 1rem;
    }

    .label {
        font-size: 0.625rem;
        font-family: $font-mono;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.2em;
        color: $color-subtle-light;
        display: block;
    }

    .value {
        font-family: $font-mono;
        font-weight: 900;
        font-size: 2.25rem;
        letter-spacing: 0.16em;
        color: $color-charcoal;
        user-select: all;
        padding: 0.25rem 0;

        @include bapps.respond-to("sm") {
            font-size: 3rem;
            letter-spacing: 0.2em;
        }
    }

    .track {
        width: 9rem;
        margin: 0 auto;
        height: 2px;
        background-color: rgba(0, 0, 0, 0.06);
        border-radius: 9999px;
        overflow: hidden;

        @include bapps.respond-to("sm") {
            width: 12rem;
        }
    }

    .bar {
        height: 100%;
        background-color: rgba(12, 13, 14, 0.4);
        border-radius: 9999px;
        transition: width 1s linear;
        width: 100%;
    }
}

.modal-action-btn {
    @include bapps.button-reset;
    width: 100%;
    padding: 0.875rem;
    border-radius: 9999px;
    background-color: $color-charcoal;
    color: #ffffff;
    font-weight: 700;
    font-size: 0.8125rem;
    box-shadow: $shadow-xs;
    @include bapps.transition-smooth;

    @include bapps.respond-to("sm") {
        padding: 1rem;
        font-size: 0.875rem;
    }

    &:hover {
        background-color: $color-charcoal-hover;
    }

    &:active {
        transform: scale(0.98);
    }
}
</style>
